import { z } from "zod";
import {
  changePasswordType,
  EditMeType,
  EditUserType,
  LoginData,
  LoginResponse,
  recoverPasswordData,
  RegisterDataType,
  ResetPasswordTypes,
  RoleEditType,
  SuccessType,
  UserWithRoles,
} from "./types";
import { createApiClient, handleGetRequest } from "../apiService";
import { useToken } from "../tokenUtils";

import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { t } from "i18next";

export const loginSchema = z.object({
  email: z.string().min(1, "El nombre de usuario es obligatorio"),
  password: z.string().min(1, t("validation.contrasena_obligatoria")),
});

export const registerSchema = z.object({
  first_name: z.string().min(1, "El nombre es obligatorio"),
  last_name: z.string().min(1, "El apellido es obligatorio"),
  institution: z.string().optional(),
  speciality: z.string().optional(),
  ORCID: z.string().optional(),
  login: z.string().email("El email no es válido"),
  password: z.string().min(1, t("validation.contrasena_obligatoria")),
});

const apiClient = createApiClient("");
const domain = getSubdomain();

const handleRequest = async <T>(
  url: string,
  data: URLSearchParams,
): Promise<T> => {
  const res = await apiClient.post<T>(url, data);
  return res.data;
};

const handlePostRequest = async <T>(
  url: string,
  data: any,
  token?: string,
): Promise<T> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
  const res = await apiClient.post<T>(url, data, { headers });
  return res.data;
};

const handlePutFileRequest = async <T>(
  url: string,
  data: FormData,
  token?: string,
): Promise<T> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "multipart/form-data",
    Authorization: token ? `Bearer ${token}` : "",
  };
  const res = await apiClient.put<T>(url, data, { headers });
  return res.data;
};

const login = async (data: LoginData): Promise<LoginResponse> => {
  const formData = new URLSearchParams();
  formData.append("username", data.email);
  formData.append("password", data.password);

  const rememberMe = data.remember ? "true" : "false";

  const res = await handleRequest<LoginResponse>(
    `/token?remember_me=${rememberMe}`,
    formData,
  );

  if (!res.access_token) throw new Error("No se recibió el token de acceso.");

  token = res.access_token;

  return res;
};

const checkUserAlreadyExists = async (user_login: string): Promise<boolean> => {
  const res = await apiClient.get<boolean>(`/users/${user_login}/exists`, {
    headers: { Accept: "application/json" },
  });
  return res.data;
};

const newUser = async (
  data: RegisterDataType,
  publicationSlug: string,
): Promise<SuccessType> => {
  registerSchema.parse(data);

  const payload = {
    first_name: data.first_name,
    last_name: data.last_name,
    login: data.login,
    password: data.password,
  };
  const res = await apiClient.post<SuccessType>(
    `/${publicationSlug}/users/new/`,
    payload,
  );
  return res.data;
};

const getMe = async (): Promise<UserWithRoles> => {
  const currentToken = useToken();
  const res = await handleGetRequest<UserWithRoles>(
    apiClient,
    `${domain}/users/me`,
    currentToken,
  );
  return res;
};

const recoverPassword = async (data: recoverPasswordData): Promise<string> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  const res = await apiClient.post<string>(`/${domain}/forget-password`, data, {
    headers,
  });
  return res.data;
};

const resetPassword = async (
  data: ResetPasswordTypes,
): Promise<SuccessType> => {
  const res = await apiClient.post<SuccessType>(
    `/${domain}/reset-password`,
    data,
  );
  return res.data;
};

const editMe = async (data: EditMeType): Promise<SuccessType> => {
  const currentToken = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.patch<SuccessType>(`/${domain}/role/me`, data, {
    headers,
  });
  return res.data;
};

const editMyRol = async (data: RoleEditType): Promise<SuccessType> => {
  const currentToken = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.patch<SuccessType>(
    `/${domain}/role/${data.login}/edit`,
    data.data,
    { headers },
  );
  return res.data;
};

const editUser = async ({
  user_login,
  data,
}: {
  user_login: string;
  data: EditUserType;
}): Promise<SuccessType> => {
  const currentToken = useToken();

  const payload = {
    first_name: data.first_name,
    last_name: data.last_name,
    login: data.email,
    email: data.email,
    id_language: data.id_language,
  };
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.patch<SuccessType>(
    `/users/${user_login}`,
    payload,
    { headers },
  );

  return res.data;
};

const changePassword = async (
  data: changePasswordType,
): Promise<SuccessType> => {
  const currentToken = useToken();

  const res = await handlePostRequest<SuccessType>(
    `/${domain}/change-password`,
    data,
    currentToken,
  );
  return res;
};

const addProfilePicture = async (data: FormData): Promise<SuccessType> => {
  const currentToken = useToken();

  return handlePutFileRequest<SuccessType>(
    `/users/me/picture`,
    data,
    currentToken,
  );
};

const logout = (): void => {
  token = null;
};
export let token: string | null = null;

const deleteUser = async (user_login: string): Promise<SuccessType> => {
  const currentToken = useToken();

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.delete<SuccessType>(`/users/${user_login}`, {
    headers,
  });

  return res.data;
};

const authService = {
  login,
  checkUserAlreadyExists,
  newUser,
  logout,
  getMe,
  recoverPassword,
  resetPassword,
  editMe,
  editMyRol,
  editUser,
  changePassword,
  addProfilePicture,
  deleteUser,
};

export default authService;
